import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'

/** Cookie max age: 24h in seconds */
export const SIDESHEET_COOKIE_MAX_AGE_24H = 60 * 60 * 24

export const SIDESHEET_COOKIE_NAMES = {
  abandonedCart: 'abandoned-cart-sidesheet',
  lastViewed: 'last-viewed-sidesheet',
  newsletter: 'newsletter-sidebar-sidesheet',
  newsletterHideDays: 'newsletter-hide-for-days',
} as const

type EvaluateOptions = { includeCart?: boolean }

/**
 * Centralized logic for header sidesheet visibility (cart, last viewed, newsletter).
 *
 * Rules (evaluated in order, at most one sidesheet per session trigger):
 * 1. Cart sidebar: when user returns with at least one item in cart. Cookie: max once per day.
 * 2. Last viewed sidebar: when cart is empty and user has viewed at least 1 PDP. Cookie: max once per day.
 * 3. Newsletter: handled in middleware (3rd page, no cart/last-viewed shown, not logged in). Cookie: max once per day. Param hideNL=<days> inhibits for x days.
 *
 * Logged-in users never see the newsletter sidesheet (enforced in middleware).
 *
 * @param hasRecentlyViewedProducts - Pass true when the user has at least one product in recently viewed (from getProductsRecentlyViewed). Caller should provide this to avoid duplicate async fetch.
 */
export const useSidesheetVisibility = (
  hasRecentlyViewedProducts: Ref<boolean>
) => {
  const cart = useCart()

  const abandonedCartCookie = useCookie(SIDESHEET_COOKIE_NAMES.abandonedCart, {
    maxAge: SIDESHEET_COOKIE_MAX_AGE_24H,
  })
  const lastViewedCookie = useCookie(SIDESHEET_COOKIE_NAMES.lastViewed, {
    maxAge: SIDESHEET_COOKIE_MAX_AGE_24H,
  })

  const { openDialog: openCartDialog } = useDialog(SIDEBAR_IDS.headerSideCart)
  const { openDialog: openLastViewedDialog } = useDialog(
    SIDEBAR_IDS.headerSideLastViewed
  )

  const shouldShowCartSidesheet = (): boolean => {
    if (abandonedCartCookie.value) return false
    if (!cart?.lineItems?.value?.length) return false
    return true
  }

  const shouldShowLastViewedSidesheet = (): boolean => {
    if (lastViewedCookie.value) return false
    if (cart?.lineItems?.value?.length) return false
    if (!hasRecentlyViewedProducts.value) return false
    return true
  }

  const evaluateSidesheetVisibility = (options?: EvaluateOptions) => {
    if (import.meta.server) return

    const { isAnyDialogOpen } = useDialog()
    const { menuStatus } = useMenu()
    if (isAnyDialogOpen?.value || menuStatus?.value?.isActive) return
    const includeCart = options?.includeCart !== false

    if (includeCart && shouldShowCartSidesheet()) {
      openCartDialog()
      abandonedCartCookie.value = 'true'
      return
    }

    if (shouldShowLastViewedSidesheet()) {
      openLastViewedDialog()
      lastViewedCookie.value = 'true'
    }
  }

  return {
    abandonedCartCookie,
    lastViewedCookie,
    evaluateSidesheetVisibility,
  }
}
